/** vendor */
import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'

/** lib */
import { Card } from '../../components/UI/Card'
import Loading from '../../components/UI/Loading'
import { useToast } from '../../hooks/useToast'

/** state */
import { getImportedRecipes } from '../../actions/user.actions'

/** components */
import SavedRecipeList from '../../components/Recipes/SavedRecipeList'
import ImportedRecipeList from '../../components/Recipes/ImportedRecipeList'
import CustomRecipeList from '../../components/Recipes/CustomRecipeList'
import NotesList from '../../components/Recipes/NotesList'
import NoteDialog from '../../components/Dialogs/NoteDialog'

import ImportForm from '../../components/Forms/ImportForm'

export default function UserRecipes() {
    // vendor utils
    const dispatch = useDispatch()

    // a9 utils
    const toast = useToast()

    // ui states
    const [importing, setImporting] = useState(false)
    const [loading, setLoading] = useState(false)
    const [tab, setTab] = useState('saved')
    const [notes, setNotes] = useState([])
    const [showNote, setShowNote] = useState(false)
    const [selectedNote, setSelectedNote] = useState(undefined)

    /**
     * Utils
     */
    const updateImportedRecipes = async () => {
        setLoading(true)

        try {
            await getImportedRecipes()
        } catch (error) {
            
        } finally {
            setLoading(false)
        }
    }

    /**
     * Hooks
     */
    useEffect(() => {
        if(tab === 'notes') {
            let notes = localStorage.getItem('notes') 
            notes = notes ? JSON.parse(notes) : []
            setNotes(notes)
        }
        // get notes in localstorage
    }, [tab])

    /**
     * Handlers
     */
    const handleOnCreateNote = (notes) => {
        setNotes(notes)
        setSelectedNote(undefined)
        setShowNote(false)
    }

    const handleOnSelectNote = (note) => {
        setSelectedNote(note)
        setShowNote(true)
    }

    const handleOnImportSuccess = async () => {
        setImporting(true)
        await updateImportedRecipes()
        toast.open(`recipe imported`)
        setImporting(false)
    }   

    const handleOnImportFail = () => {
        toast.open(`recipe import failed`)
        updateImportedRecipes()
    }

    const handleToggleTab = (e) => {
        e.preventDefault()
        setTab(e.target.href.split('#')[1])
    }

    return (
        <div className="pt-12 md:max-w-4xl w-full">
            <Card title="my recipes" autoHeight={!['imported', 'notes'].includes(tab)}>
                {
                    loading && <div className="h-[60vh] relative pt-40"><Loading /></div>
                }

                {
                    !loading &&
                        <div 
                            className="w-full flex relative flex-col items-center pb-0" 
                            style={{ height: !['imported', 'notes'].includes(tab) ? 'calc(100vh - 108px)' : 'auto' }}
                        >
                            <ul className="w-full border-b-2 border-an_orange-dark flex justify-center flex-wrap text-sm font-medium bg-an_white-dark text-center sticky p-2 pb-0 left-0 top-0">
                                <li className="w-1/4 text-center px-1">
                                    <a 
                                    href="#saved" 
                                    aria-current="page"
                                    onClick={handleToggleTab}  
                                    className={`${tab === 'saved' 
                                            ? 'active bg-an_orange text-border-orange border border-an_orange-dark border-b-0 text-an_white'
                                            : 'text-an_orange-dark'} block py-1 px-2 rounded-t  border-transparent border border-b-0`
                                        }
                                    >
                                        saved
                                    </a>
                                </li>
                                <li className="w-1/4 text-center px-1">
                                    <a 
                                    href="#custom"
                                    onClick={handleToggleTab} 
                                    className={`${tab === 'custom' 
                                            ? 'active bg-an_orange text-border-orange border border-an_orange-dark border-b-0 text-an_white'
                                            : 'text-an_orange-dark'} block py-1 px-2 rounded-t  border-transparent border border-b-0`
                                        }
                                    >
                                        custom
                                    </a>
                                </li>
                                <li className="w-1/4 text-center px-1">
                                    <a 
                                    href="#imported"
                                    onClick={handleToggleTab}
                                    className={`${tab === 'imported' 
                                        ? 'active bg-an_orange text-border-orange border border-an_orange-dark border-b-0 text-an_white'
                                        : 'text-an_orange-dark'} block py-1 px-2 rounded-t  border-transparent border border-b-0`
                                    } 
                                    >
                                        imported
                                    </a>
                                </li>
                                <li className="w-1/4 text-center px-1">
                                    <a 
                                    href="#notes"
                                    onClick={handleToggleTab}
                                    className={`${tab === 'notes' 
                                        ? 'active bg-an_orange text-border-orange border border-an_orange-dark border-b-0 text-an_white'
                                        : 'text-an_orange-dark'} block py-1 px-2 rounded-t  border-transparent border border-b-0`
                                    } 
                                    >
                                        notes
                                    </a>
                                </li>
                            </ul>
                
                            { 
                                tab === 'saved' && <SavedRecipeList /> 
                            }
                            
                            { 
                                tab === 'custom' && 
                                    <CustomRecipeList /> 
                            }
                            
                            { 
                                tab === 'imported' && 
                                    <div className="">
                                        <ImportedRecipeList />
                                    </div> 
                            }

                            {
                                tab === 'notes' &&
                                    <div className="w-full px-4">
                                        <NotesList 
                                            notes={notes} 
                                            onSelectNote={handleOnSelectNote} 
                                        />
                                    </div>
                            }
                        </div>
                }
            </Card>
            {
                tab === 'imported' &&
                    <div className="fixed bottom-0 left-0 w-screen py-4 flex justify-center">
                        <ImportForm 
                            importing={importing}
                            onImportSuccess={handleOnImportSuccess} 
                            onImportFail={handleOnImportFail} 
                        />
                    </div>
            }

            {
                tab === 'notes' && !showNote &&
                    <div className="fixed bottom-0 left-0 w-screen py-4 flex justify-center">
                        <button
                            onClick={(e) => { 
                                setShowNote(!showNote) 
                            }}
                            className="bg-an_orange border border-an_orange-dark text-an_white text-border-orange py-2 px-4 rounded" 
                            type="button"
                        >
                            {
                                loading ? 
                                    <div className="animate-pulse">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 animate-spin ">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                        </svg>
                                    </div> : 'make a note'
                            }
                        </button>
                    </div>
            }

            {
                showNote &&
                    <NoteDialog 
                        note={selectedNote} 
                        showDialog={showNote}
                        onCancel={() => {
                            setSelectedNote(undefined)
                            setShowNote(false)
                        }}
                        onCreate={handleOnCreateNote} 
                    />
            }
        </div>
    )
}